import { yupResolver } from "@hookform/resolvers/yup";
import { Text, VStack, View } from "native-base";
import React from "react";
import { useForm } from "react-hook-form";
import { StyleSheet } from "react-native";
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field";

import FormButton from "@/components/atoms/FormButton";
import FormField from "@/components/molecules/FormField";
import { colors } from "@/theme/colors";

import { CodeFormData, codeFormSchema } from "../schema";
import { RecoveryPassFormComponentProps } from "../types";

const CELL_COUNT = 6;

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 12,
  },
  cellRoot: {
    width: 54,
    height: 54,
    justifyContent: "center",
    alignItems: "center",
    borderBottomColor: "#ccc",
    borderBottomWidth: 1,
  },
  focusCell: {
    borderBottomColor: colors.primary[600],
    borderBottomWidth: 2,
  },
});

const CodeInput = ({ value, setValue }) => {
  const ref = useBlurOnFulfill({ value, cellCount: CELL_COUNT });
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  });

  return (
    <CodeField
      ref={ref}
      {...props}
      value={value}
      onChangeText={(v) => {
        if (/^[0-9]*$/gi.test(v)) setValue(v);
      }}
      cellCount={CELL_COUNT}
      rootStyle={styles.codeFieldRoot}
      keyboardType="number-pad"
      textContentType="oneTimeCode"
      renderCell={({ index, symbol, isFocused }) => (
        <View
          onLayout={getCellOnLayoutHandler(index)}
          key={index}
          style={[styles.cellRoot, isFocused && styles.focusCell]}>
          <Text fontSize="2xl">{symbol || (isFocused ? <Cursor /> : null)}</Text>
        </View>
      )}
    />
  );
};

const CodeForm = ({
  isSubmiting,
  apiError,
  onSubmit,
  onCancel,
}: RecoveryPassFormComponentProps<CodeFormData>) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CodeFormData>({
    resolver: yupResolver(codeFormSchema),
  });

  return (
    <VStack>
      <Text fontSize="lg" py="4">
        Inisira o código enviado para seu e-mail
      </Text>
      <Text pb="8">
        Lembre-se de verificar também em sua caixa de spam. O código pode demorar até 5 minutos para
        ser enviado!
      </Text>
      <FormField
        control={control}
        name="code"
        label="Digite o código"
        hint="Código válido por 10 minutos"
        required
        error={errors.code || apiError}
        render={(value, onChange) => <CodeInput value={value} setValue={onChange} />}
      />
      <FormButton isLoading={isSubmiting} mt="10" onPress={handleSubmit(onSubmit)}>
        Continuar
      </FormButton>
      <FormButton
        colorScheme="white"
        textColor="primary.700"
        my="4"
        shadow="2"
        onPress={() => onCancel()}>
        Cancelar
      </FormButton>
    </VStack>
  );
};

export default CodeForm;
